import React, { ChangeEvent, FC, useContext, useState } from 'react';
import { runInAction } from 'mobx';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import ToolTip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useGetEntityLink } from '../../routes';
import RootStoreContext from '../../../model/RootStoreContext';
import type { IEntityVersion } from '../../../model/entity/EntityVersion';
import { useMultiSelect } from '../../components/use-multi-select/useMultiSelect';
import { StatusLabel } from '../../components/tables/status-label/StatusLabel';
import { YoPublisherTable } from '../../components/yo-publisher-table/YoPublisherTable';
import { PropertyValueDisplay } from '../common/PropertyValueDisplay';
import { SpecificationProperties } from '../common/SpecificationProperties';
import { SpecificationSchedulingStatus } from '../common/SpecificationSchedulingStatus';
import type { ICheckedEntity } from './Entities';
import { EntitiesTableRowOrdering } from './EntitesTableRowOrdering';
import { EntityStatusToggleDropDownButton } from '../entity/components/EntityStatusToggleDropDownButton';
import { IEntityProperty } from '../../../model/entity/EntityProperty';

interface PropsType {
    cssColorClass: string;
    checkedEntityVersions: ICheckedEntity[];
    statusStore: any;
    pagination?: any;
}

export interface IEntityOrder {
    orderValue: string;
    orderLevel: string;
    descending: boolean;
    type: string;
}

export const EntitiesTable: FC<PropsType> = observer((props) => {

    const rootStore = useContext(RootStoreContext);
    const getEntityLink = useGetEntityLink();
    const navigate = useNavigate();
    const multiSelect = useMultiSelect<ICheckedEntity>({
        items: props.checkedEntityVersions.filter(e => props.statusStore.statusFilters.includes(e.entityVersion.entity.status)),
        selectItem: (item, selected) => {
            runInAction(() => item.checked = selected);
        }
    });
    const [order, setOrder] = useState({
        orderValue: 'name',
        orderLevel: 'entityVersion',
        descending: false,
        type: 'string'
    } as IEntityOrder);

    const handleEntityCheckboxToggle = (checked: boolean, entity: ICheckedEntity) => {
        multiSelect.handleItemSelection(entity, checked);
    };

    const handleCheckAllEntities = (e: ChangeEvent<HTMLInputElement>) => {
        multiSelect.handleSelectAll(e.target.checked);
    };

    const getPropTitlesFromKind = () => {
        return rootStore.kindVersionStore.getCurrentKindVersion().properties.map(property => {
            return <OverlayTrigger key={`overlay-${property.key}`} placement="right"
                                   overlay={<ToolTip id="button-tooltip">{property.description}</ToolTip>}>
                <th onClick={() => ordering(property.key, 'property', property.type)}
                    key={property.key}
                    className="link d-none d-md-table-cell">{property.key} {orderingIcon(property.key)}
                </th>
            </OverlayTrigger>;
        });
    };

    const entityLink = (entityVersion: IEntityVersion) => {
        return getEntityLink({ entityId: entityVersion.entity.id });
    };

    const entityRedirect = (id: number) => {
        navigate(getEntityLink({ entityId: id }));
    };

    const getEntityCellClass = (entityProperty: IEntityProperty) => {

        if (String(entityProperty.value).trim().toLowerCase() === 'true' || String(entityProperty.value).trim().toLowerCase() === 'false')
            return 'text-center';

            // if (entityProperty.value !== null && !isNaN(Number(entityProperty.value)))
        //     return 'text-end';

        else
            return 'text-start';
    };

    const entityRow = () => {

        const renderArray: any[] = [];

        if (order.orderValue)
            EntitiesTableRowOrdering(order, props.checkedEntityVersions, rootStore);

        props.checkedEntityVersions.forEach(checkedEntry => {

            renderArray.push(
                <tr key={checkedEntry.entityVersion.id}
                    className={`linked-row ${checkedEntry.entityVersion.entity.status}`}
                    data-testid={checkedEntry.entityVersion.name}
                    data-value={rootStore.kindVersionStore.getCurrentKindVersion().kind.id}>
                    <td className="td-checkbox">
                        <input type="checkbox" className={''} checked={checkedEntry.checked} data-testid="cbEntity"
                               onChange={(event) => handleEntityCheckboxToggle(event.target.checked, checkedEntry)} />
                    </td>
                    {!rootStore.kindVersionStore.getCurrentKindVersion().isStaticData &&
                        <td className={'text-center td-status'}>
                            <Link to={entityLink(checkedEntry.entityVersion)}>
                                <StatusLabel status={checkedEntry.entityVersion.entity.status} />
                            </Link>
                        </td>
                    }
                    <td data-testid="tdEntityName">
                        <Link to={entityLink(checkedEntry.entityVersion)}>
                            {checkedEntry.entityVersion.isOnOldVersionOfKind() &&
                                <OverlayTrigger key={`overlay-${checkedEntry.entityVersion.entity.id}`} placement="top"
                                                overlay={<ToolTip id="button-tooltip">Out of date with latest kind
                                                    version</ToolTip>}><i
                                    className="fas fa-exclamation-triangle me-2 text-warning" />
                                </OverlayTrigger>
                            }
                            {checkedEntry.entityVersion.name}
                        </Link>
                    </td>
                    <td className="d-none d-md-table-cell" data-testid="tdEntityDescription">
                        <Link
                            to={entityLink(checkedEntry.entityVersion)}>{checkedEntry.entityVersion.description}</Link>
                    </td>
                    {
                        rootStore.kindVersionStore.getCurrentKindVersion().properties.map((kp, index) => {

                            const entityProperty = checkedEntry.entityVersion.properties.find(ep => ep.kindPropertyKey === kp.key);

                            if (entityProperty && entityProperty.kindPropertyKindId === rootStore.kindVersionStore.getCurrentKindVersion().kind.id) {

                                return <td key={`${checkedEntry.entityVersion.id}-${kp.id}`}
                                           className={`d-none d-md-table-cell table-cell-with-cap`}
                                           data-testid={`tdEntity${kp.key}`}>
                                    <Link to={entityLink(checkedEntry.entityVersion)}>
                                        <PropertyValueDisplay kindProperty={kp} value={entityProperty.value} />
                                    </Link>
                                </td>;
                            } else
                                return <td key={`noEntityProperty-${index}+${kp.id}`}
                                           onClick={() => entityRedirect(checkedEntry.entityVersion.entity.id)}
                                           className={`d-none d-md-table-cell`} />;
                        })
                    }
                    {rootStore.kindVersionStore.getCurrentKindVersion().canUseSpecifications &&
                        <td data-testid="tdEntitySpecifications">
                            <SpecificationProperties entityLink={entityLink} entityVersion={checkedEntry.entityVersion}
                                                     entityProperties={checkedEntry.entityVersion.getSpecificationProperties().filter(p => !checkedEntry.entityVersion.getSchedulingSpecificationProperties().includes(p))}
                                                     specificationKindVersions={rootStore.specificationsForKindsStore.getKinds()} />
                        </td>
                    }
                    {rootStore.kindVersionStore.getCurrentKindVersion().canUseSpecifications &&
                        <td>
                            <SpecificationSchedulingStatus entityLink={entityLink}
                                                           entityVersion={checkedEntry.entityVersion}
                                                           entityProperties={checkedEntry.entityVersion.getSchedulingSpecificationProperties()} />
                        </td>
                    }
                    {!rootStore.kindVersionStore.getCurrentKindVersion().isStaticData &&
                        <td className="td-distributed">
                            {checkedEntry.entityVersion.inLatestDistribution && <div>Yes (in latest)</div>}
                            {!checkedEntry.entityVersion.inLatestDistribution && checkedEntry.entityVersion.entity.inAnyDistribution &&
                                <div>Yes (in previous)</div>}
                            {!checkedEntry.entityVersion.inLatestDistribution && !checkedEntry.entityVersion.entity.inAnyDistribution &&
                                <div>No</div>}
                        </td>
                    }
                </tr>
            );
        });
        return renderArray;
    };

    const orderingIcon = (propertyKey: string) =>
        order.orderValue === propertyKey ? !order.descending ? <i className="fas fa-arrow-down" /> :
            <i className="fas fa-arrow-up" /> : <span className="iconSpacer" />;

    const ordering = (orderValue: string, orderLevel: string, propertyType?: string) => {
        setOrder({
            orderValue: orderValue,
            orderLevel: orderLevel,
            descending: order.orderValue === orderValue ? !order.descending : false,
            type: propertyType ? propertyType : 'string'
        });
    };

    return (
        <div className="mdcDataTableWrapper">
            <EntityStatusToggleDropDownButton variant="outline-primary"
                                              statuses={props.statusStore.statusFilters}
                                              checkedEntityVersions={props.checkedEntityVersions}
            />
            <div className="mdcDataTableContainer">
                <YoPublisherTable data-testid="tblEntities">
                    <thead>
                    <tr>
                        <th className="td-checkbox"><input type="checkbox" className={''} id={'select-all'}
                                                           onChange={handleCheckAllEntities} />
                        </th>

                        {!rootStore.kindVersionStore.getCurrentKindVersion().isStaticData &&
                            <th className="link td-status ">
                                <div className="d-inline-block"
                                     onClick={() => ordering('status', 'entity')}>Status {orderingIcon('status')}
                                </div>

                            </th>
                        }

                        <th className="link"
                            onClick={() => ordering('name', 'entityVersion')}>Name {orderingIcon('name')}</th>
                        <th className="d-none d-md-table-cell"
                            onClick={() => ordering('description', 'entityVersion')}>Description {orderingIcon('description')}
                        </th>

                        {getPropTitlesFromKind()}

                        {rootStore.kindVersionStore.getCurrentKindVersion().canUseSpecifications &&
                            <th>Specifications</th>}
                        {rootStore.kindVersionStore.getCurrentKindVersion().canUseSpecifications && <th>Scheduling</th>}
                        {!rootStore.kindVersionStore.getCurrentKindVersion().isStaticData &&
                            <th className="td-distributed">In Distribution</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {props.checkedEntityVersions.length > 0 ? entityRow() : <tr>
                        <td colSpan={42} className="text-center">No Entities Found</td>
                    </tr>}
                    </tbody>
                </YoPublisherTable>
            </div>
            {props.pagination && <div className="paginationWrapper">{props.pagination}</div>}
        </div>
    );
});
